import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError, buildWhatsappUrl, formatRupiah, API } from "@/lib/api";
import { toast } from "sonner";
import { WhatsappLogo, Plus, Minus, ForkKnife, ShoppingCart, X, CheckCircle, ChatCircleDots } from "@phosphor-icons/react";
import { PublicFieldRenderer, DISPLAY_ONLY, ytEmbed, evalCondition } from "@/components/fieldsBuilder";

export default function PublicMenu() {
  const { slug } = useParams();
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({});
  const [activeCat, setActiveCat] = useState(null);
  const [showCart, setShowCart] = useState(false);
  const [tableNo, setTableNo] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [notes, setNotes] = useState("");
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [waUrl, setWaUrl] = useState("");

  useEffect(() => {
    api.get(`/public/menus/${slug}`)
      .then(r => {
        setMenu(r.data);
        setActiveCat(r.data.categories?.[0] || "all");
        // Init answers state for custom fields
        const init = {};
        (r.data.fields || []).forEach(f => {
          if (DISPLAY_ONLY.includes(f.type)) return;
          if (f.type === "checkbox") init[f.id] = [];
          else if (f.type === "rating") init[f.id] = 0;
          else init[f.id] = "";
        });
        setAnswers(init);
      })
      .catch(() => setMenu(false))
      .finally(() => setLoading(false));
  }, [slug]);

  const inc = (id) => setCart(c => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const dec = (id) => setCart(c => {
    const q = (c[id] || 0) - 1;
    const copy = { ...c };
    if (q <= 0) delete copy[id]; else copy[id] = q;
    return copy;
  });

  const cartItems = useMemo(() => {
    if (!menu) return [];
    return Object.entries(cart)
      .map(([id, qty]) => {
        const it = menu.items.find(i => i.id === id);
        return it ? { ...it, qty } : null;
      })
      .filter(Boolean);
  }, [cart, menu]);

  const total = cartItems.reduce((s, it) => s + it.price * it.qty, 0);
  const totalQty = cartItems.reduce((s, it) => s + it.qty, 0);

  const buildMessage = () => {
    const lines = [];
    if (menu.intro_message) lines.push(menu.intro_message, "");
    lines.push(`*${menu.name}*`);
    if (menu.require_table && tableNo) lines.push(`Meja: ${tableNo}`);
    if (customerName) lines.push(`Nama: ${customerName}`);
    lines.push("");
    lines.push("*Pesanan:*");
    cartItems.forEach(it => {
      lines.push(`• ${it.qty}x ${it.name} — ${formatRupiah(it.price * it.qty)}`);
    });
    lines.push("");
    lines.push(`*Total: ${formatRupiah(total)}*`);
    // Custom field answers (only visible ones)
    (menu.fields || []).forEach(f => {
      if (DISPLAY_ONLY.includes(f.type)) return;
      if (!evalCondition(f, answers)) return;
      let val = answers[f.id];
      if (Array.isArray(val)) val = val.join(", ");
      if (f.type === "rating") val = val ? `${val}/${f.max_rating || 5} ⭐` : "-";
      if (val === "" || val == null) val = "-";
      lines.push(`• ${f.label}: ${val}`);
    });
    if (notes) lines.push("", `Catatan: ${notes}`);
    return lines.join("\n");
  };

  const submit = async () => {
    if (cartItems.length === 0) return toast.error("Keranjang masih kosong");
    if (menu.require_table && !tableNo.trim()) return toast.error("Nomor meja wajib diisi");
    // Validate custom required fields
    for (const f of (menu.fields || [])) {
      if (DISPLAY_ONLY.includes(f.type)) continue;
      const v = answers[f.id];
      const empty = (v == null) || v === "" || (Array.isArray(v) && v.length === 0) || (f.type === "rating" && !v);
      if (f.required && empty) {
        toast.error(`"${f.label}" wajib diisi`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/menus/${slug}/order`, {
        table_number: tableNo,
        customer_name: customerName,
        notes,
        items: cartItems.map(it => ({ item_id: it.id, name: it.name, qty: it.qty, price: it.price })),
        answers,
      });
      const targetNumber = data?.routed_to?.whatsapp_number || menu.whatsapp_number;
      const url = buildWhatsappUrl(targetNumber, buildMessage());
      setWaUrl(url);
      setDone(true);
      window.open(url, "_blank");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-12 font-display text-2xl">Memuat…</div>;
  if (!menu) return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-6">
      <div className="nb-card p-8 text-center max-w-md">
        <h1 className="font-display font-black text-3xl">Menu tidak ditemukan</h1>
        <p className="text-[#4A4A4A] mt-2">Menu ini mungkin telah dihapus atau dinonaktifkan.</p>
      </div>
    </div>
  );

  if (done) return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-6">
      <div className="nb-card nb-card-lg p-8 text-center max-w-md fade-in">
        <div className="w-16 h-16 bg-[#A7E4C0] border-2 border-[#121212] rounded-full flex items-center justify-center mx-auto">
          <CheckCircle size={36} weight="bold" />
        </div>
        <h1 className="font-display font-black text-3xl mt-4">Pesanan dikirim!</h1>
        <p className="text-[#4A4A4A] mt-2">Pesanan Anda dikirim ke WhatsApp restoran. Jika belum terbuka, klik di bawah.</p>
        <a href={waUrl} target="_blank" rel="noreferrer" className="nb-btn nb-btn-primary text-lg mt-6 justify-center w-full" data-testid="public-menu-wa-button">
          <WhatsappLogo size={20} weight="bold" /> Buka WhatsApp
        </a>
        <button onClick={() => { setDone(false); setCart({}); setTableNo(""); setCustomerName(""); setNotes(""); }} className="nb-btn w-full justify-center mt-3" data-testid="public-menu-reset">
          Pesan lagi
        </button>
      </div>
    </div>
  );

  const visibleItems = (activeCat === "all" || !activeCat) ? menu.items : menu.items.filter(i => i.category === activeCat);

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-24">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="px-5 pt-8 pb-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-[#A7E4C0] border-2 border-[#121212] rounded-lg flex items-center justify-center">
              <ChatCircleDots size={16} weight="bold" />
            </div>
            <span className="font-display font-black text-lg">Layanan lainnya RS Mulya</span>
          </div>
          <div className="nb-card p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#FFD166] border-2 border-[#121212] rounded-lg flex items-center justify-center">
                <ForkKnife size={24} weight="bold" />
              </div>
              <div>
                <h1 className="font-display font-black text-2xl tracking-tight" data-testid="public-menu-name">{menu.name}</h1>
                {menu.description && <p className="text-sm text-[#4A4A4A]">{menu.description}</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Categories */}
        {menu.categories?.length > 0 && (
          <div className="px-5 mb-3 sticky top-0 bg-[#FDFBF7] z-10 py-2">
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
              {menu.categories.map(c => (
                <button
                  key={c}
                  onClick={() => setActiveCat(c)}
                  className={`nb-btn whitespace-nowrap ${activeCat === c ? "nb-btn-ink" : ""}`}
                  data-testid={`menu-cat-${c}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Items */}
        <div className="px-5 space-y-3">
          {visibleItems.length === 0 && (
            <div className="nb-card p-6 text-center text-[#4A4A4A]">Belum ada menu di kategori ini.</div>
          )}
          {visibleItems.map(it => {
            const qty = cart[it.id] || 0;
            return (
              <div key={it.id} className={`nb-card p-3 flex gap-3 ${!it.available ? "opacity-60" : ""}`} data-testid={`menu-item-${it.id}`}>
                {it.image_path ? (
                  <img src={`${API}/files/${it.image_path}`} alt={it.name} className="w-24 h-24 object-cover border-2 border-[#121212] rounded-lg shrink-0" />
                ) : (
                  <div className="w-24 h-24 bg-[#FDFBF7] border-2 border-[#121212] rounded-lg flex items-center justify-center shrink-0">
                    <ForkKnife size={26} weight="bold" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-display font-bold text-base leading-snug">{it.name}</h3>
                  {it.description && <p className="text-xs text-[#4A4A4A] mt-1 line-clamp-2">{it.description}</p>}
                  <div className="flex items-center justify-between mt-2">
                    <span className="mono font-bold">{formatRupiah(it.price)}</span>
                    {it.available ? (
                      qty === 0 ? (
                        <button onClick={() => inc(it.id)} className="nb-btn nb-btn-primary !py-1 !px-3" data-testid={`add-item-${it.id}`}>
                          <Plus size={14} weight="bold" /> Tambah
                        </button>
                      ) : (
                        <div className="flex items-center gap-2">
                          <button onClick={() => dec(it.id)} className="nb-btn !py-1 !px-2" data-testid={`dec-item-${it.id}`}><Minus size={14} weight="bold" /></button>
                          <span className="font-bold w-5 text-center" data-testid={`qty-item-${it.id}`}>{qty}</span>
                          <button onClick={() => inc(it.id)} className="nb-btn !py-1 !px-2" data-testid={`inc-item-${it.id}`}><Plus size={14} weight="bold" /></button>
                        </div>
                      )
                    ) : (
                      <span className="nb-badge bg-gray-200 text-xs">Habis</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Sticky cart bar */}
      {totalQty > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40">
          <div className="max-w-md mx-auto p-3">
            <button onClick={() => setShowCart(true)} className="w-full bg-[#A7E4C0] border-2 border-[#121212] rounded-xl shadow-[4px_4px_0_#121212] p-4 flex items-center justify-between font-bold" data-testid="open-cart-button">
              <span className="flex items-center gap-2">
                <ShoppingCart size={20} weight="bold" /> {totalQty} item
              </span>
              <span className="flex items-center gap-2">
                {formatRupiah(total)} <WhatsappLogo size={20} weight="bold" />
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Cart drawer */}
      {showCart && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end justify-center" onClick={() => setShowCart(false)}>
          <div className="bg-[#FDFBF7] w-full max-w-md border-t-2 border-x-2 border-[#121212] rounded-t-2xl p-5 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center">
              <h2 className="font-display font-black text-2xl">Konfirmasi Pesanan</h2>
              <button onClick={() => setShowCart(false)} className="nb-btn" data-testid="close-cart-button"><X size={18} weight="bold" /></button>
            </div>

            <div className="mt-4 space-y-2">
              {cartItems.map(it => (
                <div key={it.id} className="flex justify-between items-center border-b border-[#121212]/20 pb-2">
                  <div>
                    <p className="font-bold">{it.name}</p>
                    <p className="text-xs text-[#4A4A4A]">{it.qty} × {formatRupiah(it.price)}</p>
                  </div>
                  <p className="mono font-bold">{formatRupiah(it.qty * it.price)}</p>
                </div>
              ))}
              <div className="flex justify-between items-center pt-2 font-display font-black text-xl">
                <span>Total</span>
                <span data-testid="cart-total">{formatRupiah(total)}</span>
              </div>
            </div>

            <div className="space-y-3 mt-5">
              {menu.require_table && (
                <div>
                  <label className="nb-label">Nomor Meja *</label>
                  <input className="nb-input" value={tableNo} onChange={e => setTableNo(e.target.value)} placeholder="Mis. 4" data-testid="cart-table-input" />
                </div>
              )}
              <div>
                <label className="nb-label">Nama (opsional)</label>
                <input className="nb-input" value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Nama Anda" data-testid="cart-name-input" />
              </div>
              <div>
                <label className="nb-label">Catatan (opsional)</label>
                <textarea rows={2} className="nb-input" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Tanpa bawang, level pedas, dll" data-testid="cart-notes-input" />
              </div>

              {(menu.fields || []).length > 0 && (
                <div className="pt-2 border-t-2 border-[#121212]/10 space-y-3" data-testid="cart-custom-fields">
                  {(menu.fields || []).filter(f => evalCondition(f, answers)).map(f => (
                    <PublicFieldRenderer
                      key={f.id}
                      field={f}
                      value={answers[f.id]}
                      onChange={(v) => setAnswers(a => ({ ...a, [f.id]: v }))}
                      ytEmbed={ytEmbed}
                    />
                  ))}
                </div>
              )}
            </div>

            <button onClick={submit} disabled={submitting} className="nb-btn nb-btn-primary w-full justify-center text-lg mt-5" data-testid="cart-submit-button">
              <WhatsappLogo size={20} weight="bold" /> {submitting ? "Memproses…" : "Pesan via WhatsApp"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
